/* Workbench routing (W6) — the flag gate and the old-route redirects.
 *
 * FLAG OFF (or still probing) the officer gets the CURRENT
 * TasksWorkbench, byte-identical: ``TasksRoute`` renders it directly,
 * with no wrapper element of its own. Only a resolved ``true`` moves
 * anybody to the new surface.
 *
 * FLAG ON the old TasksWorkbench routes redirect BY TASK TYPE, so no
 * pilot-console bookmark and no deep link dead-ends:
 *   /v2/tasks             → /v2/workbench
 *   /v2/tasks?task=<id>   → /v2/workbench/<surface-of-that-type>?task=<id>
 */

import { useEffect, useState } from 'react'
import { Navigate, useLocation, useParams, useSearchParams } from 'react-router-dom'
import { apiFetch } from '../../lib/api'
import type { TaskQueueItem } from '../../lib/tasks'
import TasksWorkbench from '../TasksWorkbench'
import { useRkDecksEnabled } from '../rkdecks/api'
import WorkbenchHome from './WorkbenchHome'
import ContextWorkbenchRoute from './ContextWorkbenchRoute'
import { useContextualNavigation } from '../navigation/useRead'
import { ErrorState, Skeleton } from '../state'
import { SURFACE_SLUGS, modeOf, surfaceFor } from './placement'

/** /v2/workbench — dark while the flag is off (the officer's surface is
 *  still the tasks workbench, so send them there rather than showing a
 *  disabled shell). */
/* While the flag probes, the page area is never white (state system):
 * the landing's own shape, five tiles high, until the answer lands. */
function ProbingWorkbench() {
  return (
    <main className="wb">
      <div className="inner">
        <Skeleton kind="tiles" />
      </div>
    </main>
  )
}

export function WorkbenchGate() {
  const enabled = useRkDecksEnabled()
  const contextual = useContextualNavigation()
  if (enabled === null) return <ProbingWorkbench />
  if (!enabled) return <Navigate to="/v2/tasks" replace />
  // Mounting the legacy page during this probe lets its remembered-fund
  // effect rewrite an explicit contextual URL before the answer arrives.
  if (contextual.loading) return <ProbingWorkbench />
  if (contextual.error)
    return (
      <main className="wb">
        <ErrorState
          what="workbench settings"
          error={contextual.error}
          onRetry={() => window.location.reload()}
        />
      </main>
    )
  if (contextual.value?.enabled) return <ContextWorkbenchRoute />
  return <WorkbenchHome />
}

/** /v2/tasks — the old surface, and the redirect out of it. */
export function TasksRoute() {
  const enabled = useRkDecksEnabled()
  const [params] = useSearchParams()
  const taskId = params.get('task')
  const [target, setTarget] = useState<string | null>(null)

  useEffect(() => {
    if (enabled !== true) return
    if (!taskId) {
      setTarget('/v2/workbench')
      return
    }
    let cancelled = false
    // Redirect BY TASK TYPE — the placement oracle decides which surface
    // owns the row, so a bookmark lands where the work actually lives.
    apiFetch<TaskQueueItem>(`/api/tasks/${taskId}`)
      .then((t) => {
        if (cancelled) return
        const mode = modeOf(t.task_type)
        // A document state is not workbench work at all — its home is
        // the Documents page, where acting on it mints a request.
        if (mode === 'document_state') return setTarget('/v2/documents')
        const surface = surfaceFor(t.task_type)
        if (!surface) return setTarget('/v2/workbench')
        const slug = SURFACE_SLUGS[surface]
        // A system failure is never a selectable row: land on the
        // surface that shows it, without preselecting anything.
        if (mode !== 'queue') return setTarget(`/v2/workbench/${slug}`)
        setTarget(`/v2/workbench/${slug}?task=${encodeURIComponent(taskId)}`)
      })
      .catch(() => {
        if (!cancelled) setTarget('/v2/workbench')
      })
    return () => {
      cancelled = true
    }
  }, [enabled, taskId])

  // Flag off, or still probing: the current workbench, untouched.
  if (enabled !== true) return <TasksWorkbench />
  if (!target) return <ProbingWorkbench />
  return <Navigate to={target} replace />
}

/** /workbench and /workbench/<surface> — the spec's deep-link form,
 *  forwarded into the v2 shell with its query string intact. */
export function WorkbenchDeepLink() {
  const { surface } = useParams()
  const { search } = useLocation()
  return <Navigate to={`/v2/workbench${surface ? `/${surface}` : ''}${search}`} replace />
}
